'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { WooCommerceProduct } from '@/types/woocommerce';
import { transformGraphQLProduct } from '@/utils/transformGraphQLProduct';
import { isAuthenticated } from '@/lib/apollo-client-simple';

const WORDPRESS_URL = process.env.NEXT_PUBLIC_WORDPRESS_URL || process.env.WORDPRESS_URL || 'https://demo2.beauty.local';

export interface CartItem {
  product: WooCommerceProduct;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: WooCommerceProduct | any, quantity: number) => Promise<void>;
  removeFromCart: (productId: number | string) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  isLoading: boolean;
  itemCount: number;
  total: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Helper function to check if product is in GraphQL format
const isGraphQLProduct = (product: any): boolean => {
  return (
    (product.regularPrice !== undefined || product.salePrice !== undefined || product.onSale !== undefined) &&
    (product.regular_price === undefined && product.sale_price === undefined)
  ) || (
    product.image?.sourceUrl !== undefined && 
    (!product.images || product.images.length === 0 || !product.images[0]?.src)
  );
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const hasLoadedFromAPI = useRef(false);

  // Initial state is empty on both server and client. localStorage is read
  // inside a mount effect below, so the initial client render matches the SSR
  // output and React can hydrate cleanly (otherwise error #418 fires because
  // server-rendered [] wouldn't match client-rendered populated-cart). The
  // cart populates immediately after hydration — visually indistinguishable
  // from lazy-init, but hydration-safe.
  const [items, setItems] = useState<CartItem[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const savedCart = localStorage.getItem('cart');
    if (!savedCart) return;
    try {
      const parsedCart = JSON.parse(savedCart);
      const transformedCart = parsedCart.map((item: CartItem) => ({
        ...item,
        product: isGraphQLProduct(item.product)
          ? transformGraphQLProduct(item.product)
          : item.product
      }));
      setItems(transformedCart);
    } catch (e) {
      console.error('[CartContext] Failed to load cart from localStorage:', e);
    }
  }, []);

  // Load cart from WordPress if user is authenticated
  // Cart is source-of-truth in localStorage regardless of auth state. The
  // WordPress-side cart sync (syncCartToWordPress) hit endpoints that were
  // never registered (/cart/update, /cart/remove → 404) AND used the variant
  // slug as product_id (parseInt → NaN), so it persisted nothing anyway. The
  // checkout flow passes cart_items directly in the POST body, so server-side
  // persistence isn't required for order creation. Disabled to stop the loop.

  // Save cart to localStorage whenever it changes — no WP sync.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('cart', JSON.stringify(items));
    } catch (e) {
      console.error('[CartContext] Failed to save cart to localStorage:', e);
    }
  }, [items]);

  const loadCartFromWordPress = useCallback(async () => {
    if (!isAuthenticated()) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth-token');
      const response = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/cart`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.items) {
          setItems(data.items);
        }
      }
    } catch (error) {
      console.error('Failed to load cart from WordPress:', error);
      // Fallback to localStorage
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart);
          const transformedCart = parsedCart.map((item: CartItem) => ({
            ...item,
            product: isGraphQLProduct(item.product)
              ? transformGraphQLProduct(item.product)
              : item.product
          }));
          setItems(transformedCart);
        } catch (e) {
          console.error('Failed to load cart from localStorage:', e);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const syncCartToWordPress = useCallback(async () => {
    if (!isAuthenticated() || syncing) return;

    setSyncing(true);
    try {
      const token = localStorage.getItem('auth-token');

      // Get current WordPress cart
      const getResponse = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/cart`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (getResponse.ok) {
        const data = await getResponse.json();
        const wpCart: {[key: string]: number} = {};

        if (data.success && data.items) {
          data.items.forEach((item: CartItem) => {
            const productId = typeof item.product.id === 'string'
              ? parseInt(item.product.id) || item.product.id
              : item.product.id;
            wpCart[productId] = item.quantity;
          });
        }

        const localCart: {[key: string]: number} = {};
        items.forEach(item => {
          const productId = typeof item.product.id === 'string'
            ? parseInt(item.product.id) || item.product.id
            : item.product.id;
          localCart[productId] = item.quantity;
        });

        // Update items with different quantities or add new items
        for (const [productId, quantity] of Object.entries(localCart)) {
          if (wpCart[productId] !== quantity) {
            await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/cart/update`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ product_id: parseInt(productId), quantity }),
            });
          }
        }

        // Remove items that are in WordPress but not in local
        for (const productId of Object.keys(wpCart)) {
          if (!localCart[productId]) {
            await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/cart/remove`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ product_id: parseInt(productId) }),
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to sync cart to WordPress:', error);
    } finally {
      setSyncing(false);
    }
  }, [items, syncing]);

  const addToCart = useCallback(async (product: WooCommerceProduct | any, quantity: number) => {
    setIsLoading(true);
    try {
      // Transform product if it's in GraphQL format
      const transformedProduct = isGraphQLProduct(product)
        ? transformGraphQLProduct(product)
        : product;

      setItems((prevItems) => {
        const existingItem = prevItems.find((item) => {
          // Match by ID, handling both numeric IDs and string IDs
          const itemId = typeof item.product.id === 'string'
            ? parseInt(item.product.id) || item.product.id
            : item.product.id;
          const productId = typeof transformedProduct.id === 'string'
            ? parseInt(transformedProduct.id) || transformedProduct.id
            : transformedProduct.id;
          return itemId === productId;
        });

        let newItems;
        if (existingItem) {
          newItems = prevItems.map((item) => {
            const itemId = typeof item.product.id === 'string'
              ? parseInt(item.product.id) || item.product.id
              : item.product.id;
            const productId = typeof transformedProduct.id === 'string'
              ? parseInt(transformedProduct.id) || transformedProduct.id
              : transformedProduct.id;
            return itemId === productId
              ? { ...item, quantity: item.quantity + quantity }
              : item;
          });
        } else {
          newItems = [...prevItems, { product: transformedProduct, quantity }];
        }

        return newItems;
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeFromCart = useCallback((productId: number | string) => {
    // Compare by coerced string so variant-slug ids (non-numeric) match whether
    // the caller passes a number, slug, or anything else.
    const targetId = String(productId);
    setItems((prevItems) => prevItems.filter((item) => String(item.product.id) !== targetId));
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setItems((prevItems) =>
      prevItems.map((item) => {
        const itemId = typeof item.product.id === 'string'
          ? parseInt(item.product.id) || item.product.id
          : item.product.id;
        return itemId === productId ? { ...item, quantity } : item;
      })
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  // Memoize itemCount calculation
  const itemCount = useMemo(() => {
    return items.reduce((acc, item) => acc + item.quantity, 0);
  }, [items]);

  // Memoize total calculation
  const total = useMemo(() => {
    return items.reduce((acc, item) => {
      // Handle both WooCommerce format (price, regular_price, sale_price) and GraphQL format (price, regularPrice, salePrice)
      const price = item.product.price ||
                    item.product.regular_price ||
                    (item.product as any).regularPrice ||
                    '0';
      const salePrice = item.product.sale_price || (item.product as any).salePrice;
      // Use sale price if on sale, otherwise use regular price
      const finalPrice = (item.product.on_sale || (item.product as any).onSale) && salePrice
        ? salePrice
        : price;
      return acc + parseFloat(finalPrice || '0') * item.quantity;
    }, 0);
  }, [items]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    isLoading,
    itemCount,
    total,
  }), [items, addToCart, removeFromCart, updateQuantity, clearCart, isLoading, itemCount, total]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
